/* eslint-disable no-duplicate-imports */
import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync } from '@msdyn365-commerce/core';
import * as React from 'react';
import { useEffect } from 'react';
import Alert from '../../components/alert';
import OrderInformation from './components/order-information-section';
import { IOrderDetailsViewProps } from './order-details';

export const OrderDetailsOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    salesId,
    receiptId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {channelAddress}
        {channelReferenceId}
        {salesId}
        {receiptId}
        {createdDate}
        {count}
        {amount}
    </Node>
);

export const OrderDetailsSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton, errors }) => (
    <Node {...salesLineProps}>
        {salesLine}
        {buyAgainButton}
        {errors}
    </Node>
);

export const OrderDetailsGroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
    </Node>
);

export const OrderDetailsGroup: React.FC<IGroup> = ({
    groupProps,
    delivery,
    address,
    salesLinesProps,
    salesLines,
    isCashAndCarryTransaction
}) => (
    <Node {...groupProps}>
        {delivery && <OrderDetailsGroupDelivery {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>
                        {salesLine.data.deliveryType === 'ship' && salesLine.data.shipment ? salesLine.trackingInfo : null}
                        <OrderDetailsSalesLine {...salesLine} />
                        {!isCashAndCarryTransaction && salesLine.salesStatus}
                    </React.Fragment>
                ))}
            </Node>
        )}
        {!delivery.showTimeslot && address}
        {delivery.showTimeslot && (
            <Node {...delivery.pickupProps}>
                {address}
                {delivery.pickupDateTimeslot}
            </Node>
        )}
    </Node>
);

export const OrderDetailsGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderDetailsGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderDetailsOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const OrderDetailsPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderDetailsHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    // No "orderDetails" namesspace because names are fixed in core interface
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderDetailsView: React.FC<IOrderDetailsViewProps> = ({
    moduleProps,
    heading,
    loading,
    orderDetails,
    resources,
    context,
    props,
    localizationState
}) => {
    useEffect(() => {
        const breadcrumbs = document.querySelectorAll('.ms-breadcrumb_link');
        if (breadcrumbs.length > 1) {
            const ordersBreadcrumb = breadcrumbs[1];

            if (ordersBreadcrumb) {
                const ordersUrl = getUrlSync('orders', context.actionContext);
                if (ordersUrl) {
                    const url = new URL(ordersUrl, window.location.origin);

                    const rawSearch = window.location.search;

                    const orderedParams: string[] = [];
                    rawSearch
                        .substring(1)
                        .split('&')
                        .forEach(param => {
                            const [key, value] = param.split('=');
                            if (key && key !== 'transactionId') {
                                orderedParams.push(`${key}=${value}`);
                            }
                        });

                    if (orderedParams.length > 0) {
                        url.search = `?${orderedParams.join('&')}`;
                    }
                    ordersBreadcrumb.setAttribute('href', url.pathname + url.search);
                }
            }
        }
    });

    if (!!orderDetails?.OrderDetail) {
        return (
            <Module {...moduleProps}>
                {heading}
                {loading}
                {orderDetails && resources && (
                    <OrderInformation
                        props={props}
                        context={context}
                        orderDetails={orderDetails}
                        resources={resources}
                        localizationState={localizationState}
                    />
                )}
            </Module>
        );
    } else {
        return (
            <Module {...moduleProps}>
                <Alert message={resources.globalErrorServiceNotAvailable} type='warning' iconSize={1} />
            </Module>
        );
    }
};

export default OrderDetailsView;
